import updateLockStore from '@/modules/core/stores/update-lock';
import type { PwaMeta } from '@/modules/core/types/PwaMeta';
import logger from '@/modules/logger/constants/logger';
import axios, { AxiosError } from 'axios';
import { compare } from 'compare-versions';
import { isNil, merge } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { match } from 'ts-pattern';
import { useRegisterSW } from 'virtual:pwa-register/react';
import type { PartialDeep } from 'type-fest';
import { useTranslation } from 'react-i18next';
import ErrorMaintenance from '@/modules/core/routes/error-maintenance';

const UPDATE_CHECK_INTERVAL = 60;

function createDummyPwaMeta(
  overrides?: PartialDeep<PwaMeta & { cancelled?: boolean }>
): PwaMeta & { cancelled?: boolean } {
  return merge(
    {
      hasUpdate: false,
      cancelled: false,
      commands: [],
      version: 'N/A',
      chromiumLikeVersion: 'N/A',
      versionInfo: {
        major: 0,
        minor: 0,
        patch: 0,
        build: [],
        environment: 'N/A',
      },
      core: {
        version: 'N/A',
        versionInfo: {
          major: 0,
          minor: 0,
          patch: 0,
          build: [],
          environment: 'N/A',
        },
      },
    } satisfies PwaMeta & { cancelled?: boolean },
    overrides ?? {}
  ) as PwaMeta & { cancelled?: boolean };
}

export default function PWAManager({
  currentVersion,
  children,
}: PropsWithChildren<{
  enabled?: boolean;
  currentVersion: string;
}>) {
  const { t, i18n } = useTranslation();
  const [maintenanceInProgress, setMaintenanceInProgress] = useState<{
    inProgress: boolean;
    message?: {
      title?: string;
      description?: string;
    };
  }>({
    inProgress: false,
  });

  const checkForUpdate = useCallback(
    async ({ abortSignal }: { abortSignal: AbortSignal }) => {
      logger.debug('core.pwa', 'Checking for update...');
      const resp = await axios
        .get<Omit<PwaMeta, 'hasUpdate'>>(`/meta.json?ts=${Date.now()}`, {
          headers: {
            'Cache-Control': 'no-store',
          },
          signal: abortSignal,
        })
        .then((res) => res.data)
        .catch((e: Error) => {
          logger.error('core.pwa', e);
          if (axios.isCancel(e)) return { CANCELLED: true };
          if (e instanceof AxiosError && e.response?.status === 404)
            return { FORCE_UPDATE: true };
          return e;
        });

      if (
        !isNil(resp) &&
        typeof resp === 'object' &&
        'CANCELLED' in resp &&
        resp.CANCELLED === true
      ) {
        return createDummyPwaMeta({ cancelled: true });
      }

      // if the meta.json is no longer available (i.e. 404 or removed),
      // it could be that the app is compiled with a different version of the code,
      // so we need to force update the app by returning 'hasUpdate' to true
      if (
        !isNil(resp) &&
        typeof resp === 'object' &&
        'FORCE_UPDATE' in resp &&
        resp.FORCE_UPDATE === true
      ) {
        return createDummyPwaMeta({
          hasUpdate: true,
          commands: [
            { cmd: 'clear_caches', runOn: 'hasUpdate' },
            // { cmd: 'reinstall_service_worker', runOn: 'hasUpdate' },
            { cmd: 'reload_window', delay: 1500, runOn: 'hasUpdate' },
          ],
        });
      }

      if (
        typeof resp !== 'object' ||
        resp === undefined ||
        resp === null ||
        'chromiumLikeVersion' in resp === false
      )
        return createDummyPwaMeta();
      const hasUpdate = compare(resp.chromiumLikeVersion, currentVersion, '!=');
      return { ...resp, hasUpdate };
    },
    [currentVersion]
  );

  const uninstallServiceWorker = useCallback(async () => {
    if (navigator.serviceWorker !== undefined) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => registration.unregister())
      );
    }
  }, []);

  const clearCaches = useCallback(async () => {
    if (window.caches !== undefined) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  }, []);

  const executeCommand = useCallback(
    async (cmd: PwaMeta['commands'][number]) => {
      match(cmd)
        .with({ cmd: 'clear_caches' }, async () => {
          logger.debug('core.pwa', 'Executing command: clear_caches');
          await clearCaches();
        })
        .with({ cmd: 'reinstall_service_worker' }, async () => {
          logger.debug(
            'core.pwa',
            'Executing command: reinstall_service_worker'
          );
          await uninstallServiceWorker();
          await clearCaches();
        })
        .with({ cmd: 'reload_window' }, async () => {
          logger.debug('core.pwa', 'Executing command: reload_window');
          window.location.reload();
        })
        .exhaustive();
    },
    [clearCaches, uninstallServiceWorker]
  );

  const executeCommands = useCallback(
    async (commands: PwaMeta['commands']) => {
      if (commands.length === 0) {
        logger.debug('core.pwa', 'No commands to execute');
        return;
      }

      logger.debug('core.pwa', 'Executing commands...');
      try {
        for (const cmd of commands) {
          if (cmd.delay !== undefined)
            await new Promise((resolve) => setTimeout(resolve, cmd.delay));
          await executeCommand(cmd);
        }
      } catch (e) {
        logger.error('core.pwa', e);
      }
    },
    [executeCommand]
  );

  const checkUpdateAndInstallUpdate = useCallback(
    async ({
      abortSignal,
      gracePeriod = 0,
    }: {
      abortSignal: AbortSignal;
      gracePeriod?: number;
    }) => {
      checkForUpdate({ abortSignal }).then(async (meta) => {
        if (meta.cancelled === true) return;
        if (meta.maintenanceInProgress === true) {
          setMaintenanceInProgress({
            inProgress: true,
            message: meta.maintenanceMessage?.[i18n.language] ??
              meta.maintenanceMessage?.['en'] ?? {
                title: t('error_maintenance.title'),
                description: t('error_maintenance.description'),
              },
          });
        } else if (
          maintenanceInProgress.inProgress &&
          (meta.maintenanceInProgress === false ||
            meta.maintenanceInProgress === undefined)
        ) {
          setMaintenanceInProgress({
            inProgress: false,
            message: undefined,
          });
          window.location.reload();
        }

        // wait for the grace period to pass after detecting update
        // in case, user do something like checkout order, we don't want to interrupt the flow
        if (meta.hasUpdate && gracePeriod > 0) {
          logger.debug(
            'core.pwa',

            `Update detected, waiting for grace period ${gracePeriod}s to pass...`
          );
          await new Promise((resolve) =>
            setTimeout(resolve, gracePeriod * 1000)
          );
        }
        if (updateLockStore.getState().isLocked === true) {
          if (gracePeriod > 0) {
            logger.debug(
              'core.pwa',
              'Grace period passed, but update lock exists, skipping...'
            );
          }
          return;
        }
        if (gracePeriod > 0) {
          logger.debug(
            'core.pwa',

            'Grace period passed and no update lock, executing update commands...'
          );
        }

        logger.debug('core.pwa', 'Got updated metadata');
        logger.debug(
          'core.pwa',

          `Current version: ${currentVersion}, Next version: ${meta.chromiumLikeVersion}, Has update: ${meta.hasUpdate}`
        );
        const commandsToBeExecuted = meta.commands.filter((cmd) => {
          const shouldRun = match<PwaMeta['commands'][number], boolean>(cmd)
            .with({ runOn: 'always' }, () => true)
            .with({ runOn: 'hasUpdate' }, () => meta.hasUpdate)
            .with({ runOn: 'noUpdate' }, () => !meta.hasUpdate)
            .with({ runOn: undefined }, () => true)
            .otherwise(() => false);

          if (!shouldRun) {
            logger.debug(
              'core.pwa',

              'Skipping command:',
              JSON.stringify(cmd.cmd),
              'since `runOn` is set to',
              JSON.stringify(cmd.runOn)
            );
          }
          return shouldRun;
        });

        if (commandsToBeExecuted.length > 0)
          logger.debug('core.pwa', 'PWA Manager will execute these commands:');
        for (const cmd of commandsToBeExecuted)
          logger.debug('core.pwa', 'Command:', cmd);
        executeCommands(commandsToBeExecuted);
      });
    },
    [
      checkForUpdate,
      currentVersion,
      executeCommands,
      i18n.language,
      maintenanceInProgress.inProgress,
      t,
    ]
  );

  const counterRef = useRef<number>(0);

  useEffect(() => {
    // if (import.meta.env.PROD === false) return;
    const abortController = new AbortController();

    // run the first update check immediately
    updateLockStore.getState().lockUpdate();
    checkUpdateAndInstallUpdate({
      abortSignal: abortController.signal,
    }).finally(() => {
      updateLockStore.getState().unlockUpdate();
    });

    const intervalId = setInterval(() => {
      counterRef.current++;

      // logger.debug('core.pwa', `Next update check in ${UPDATE_CHECK_INTERVAL - counterRef.current}s`);
      if (counterRef.current < UPDATE_CHECK_INTERVAL) return;

      counterRef.current = 0;

      logger.debug('core.pwa', 'Running periodic update check...');
      if (updateLockStore.getState().isLocked) {
        logger.debug(
          'core.pwa',

          `Update lock exists, skipping periodic update check (next check in ${UPDATE_CHECK_INTERVAL}s)`
        );
        return;
      }

      updateLockStore.getState().lockUpdate();
      checkUpdateAndInstallUpdate({
        abortSignal: abortController.signal,
        gracePeriod: 10,
      }).finally(() => {
        updateLockStore.getState().unlockUpdate();
      });
    }, 1000);

    return () => {
      logger.debug('core.pwa', 'Cancelling update process...');
      abortController.abort();
      clearInterval(intervalId);
      updateLockStore.getState().unlockUpdate();
    };
  }, [checkUpdateAndInstallUpdate]);

  useRegisterSW({
    immediate: false,
    onRegisteredSW(_swUrl, registration) {
      if (registration === undefined) return;

      logger.info('core.pwa', 'Service Worker registered.');
    },
  });

  return maintenanceInProgress.inProgress ? (
    <ErrorMaintenance message={maintenanceInProgress.message} />
  ) : (
    children
  );
}
